import { ReactNode, useMemo, memo, useId } from "react";

import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import {
  alpha,
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import { stringAvatar } from "@/components/Procedures/Checklist/utils/Helpers";
import { feedbackCharCount } from "@/helpers/components/CommentSuggestion/Variables";

import {
  ProcedureNodeStepComment,
  SuggestionResponse,
} from "@/model/Procedure/ProcedureNodeStepComment";

type Props = {
  title: ReactNode;
  commentId?: string;
  comment?: ProcedureNodeStepComment;
  suggestion?: SuggestionResponse;
  children: ReactNode;
  isExpanded: boolean;
  toggleExpand: () => void;
  isDeletable?: boolean;
  onDelete?: () => void;
  chip?: ReactNode;
  lineClamp?: number;
};

const clampStyles = (lines: number) => ({
  display: "-webkit-box",
  WebkitLineClamp: lines,
  WebkitBoxOrient: "vertical" as const,
  overflow: "hidden",
});

export const FeedbackCard = memo((props: Props) => {
  const {
    comment,
    suggestion,
    children,
    title,
    isExpanded,
    toggleExpand,
    isDeletable,
    onDelete,
    chip,
    lineClamp = 3,
  } = props;

  const headerId = useId();

  const isComment = Boolean(comment);
  const isSuggestion = Boolean(suggestion);

  const contentLength = useMemo(() => {
    if (isComment) return comment?.Value?.length ?? 0;
    if (isSuggestion) return suggestion?.Description?.length ?? 0;
    return 0;
  }, [isComment, isSuggestion, comment?.Value, suggestion?.Description]);

  const attachments = useMemo(
    () => (comment?.Attachments ?? suggestion?.Attachments ?? []),
    [comment?.Attachments, suggestion?.Attachments],
  );

  const hasAttachments = attachments.length > 0;
  const shouldShowExpand = contentLength > feedbackCharCount || hasAttachments;

  const user = useMemo(
    () =>
      comment?.UserName ??
      (suggestion?.Anonymous ? "Anonymous" : suggestion?.CreatedBy?.Name ?? "—"),
    [comment?.UserName, suggestion?.Anonymous, suggestion?.CreatedBy?.Name],
  );

  const fileDisplay = (url: string) => {
    try {
      const u = new URL(url, typeof window === "undefined" ? "http://localhost" : window.location.origin);
      const raw = u.pathname.split("/").pop() || url;
      return decodeURIComponent(raw);
    } catch {
      return url;
    }
  };

  return (
    <Box sx={{ position: "relative", mx: { md: 1 }, my: 1 }}>
      <Card
        variant="outlined"
        sx={(theme) => ({
          width: "100%",
          pb: 2,
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "none",
          borderColor: alpha(theme.palette.divider, 0.6),
          transition: "box-shadow .2s ease, border-color .2s ease",
          "&:hover": {
            boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
            borderColor: alpha(theme.palette.primary.main, 0.25),
          },
        })}
        aria-labelledby={headerId}
      >
        <CardContent sx={{ p: 2.25 }}>
          {/* Header */}
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1 }}>
            <Tooltip title={user}>
              <Avatar
                {...stringAvatar(user)}
                alt={user}
                sx={{ width: 36, height: 36, border: (t) => `1px solid ${alpha(t.palette.divider, 0.8)}` }}
              />
            </Tooltip>

            <Typography id={headerId} variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {title}
            </Typography>

            {chip && (
              <Box sx={{ ml: 0.5 }}>
                {typeof chip === "string" ? (
                  <Chip size="small" variant="outlined" label={chip} sx={{ borderRadius: 1.5 }} />
                ) : (
                  chip
                )}
              </Box>
            )}

            <Box sx={{ ml: "auto" }} />

            {/* Actions */}
            <Stack
              direction="row"
              spacing={0.5}
              sx={{
                "& .MuiIconButton-root": {
                  p: 0.5,
                  transition: "transform .15s ease, background-color .15s ease, box-shadow .15s ease",
                  borderRadius: 2,
                  "&:hover": {
                    transform: "translateY(-1px)",
                    backgroundColor: (t) => alpha(t.palette.action.hover, 0.6),
                    boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                  },
                  "&:focus-visible": {
                    outline: "none",
                    boxShadow: (t) => `0 0 0 3px ${alpha(t.palette.info.main, 0.35)}`,
                  },
                },
              }}
            >
              {isDeletable && (
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={onDelete} aria-label="delete">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {shouldShowExpand && (
                <Tooltip title={isExpanded ? "Show less" : "Show more"}>
                  <IconButton
                    size="small"
                    onClick={toggleExpand}
                    aria-label={isExpanded ? "collapse" : "expand"}
                    aria-expanded={isExpanded}
                  >
                    {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Stack>

          {/* Body */}
          <Box sx={isExpanded ? undefined : clampStyles(lineClamp)}>
            {children}
          </Box>

          {/* Attachments */}
          {hasAttachments && (
            <>
              <Divider sx={{ my: 1.25 }} />
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                {attachments.map((att, idx) => (
                  <Chip
                    key={idx}
                    size="small"
                    variant="outlined"
                    icon={<DescriptionOutlinedIcon />}
                    label={
                      <Typography component="span" noWrap maxWidth={220}>
                        {fileDisplay(att.Url)}
                      </Typography>
                    }
                    component={Link as any}
                    href={att.Url}
                    target="_blank"
                    rel="noopener noreferrer"
                    clickable
                    sx={{
                      borderRadius: 1.5,
                      "& .MuiChip-icon": { fontSize: 18 },
                    }}
                  />
                ))}
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
});
